import * as React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from "@/hooks/use-toast";
import { useWalletAlerts } from "@/hooks/use-wallet-alerts";
import { useAuth } from "@/hooks/use-auth";
import { createWalletAlert } from "@/lib/wallet-alert-api";
import { AlertLimitDialog } from "@/components/alert-limit-dialog";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { config } from "@/config";

// Form schema for wallet alert creation
const walletAlertSchema = z.object({
  walletAddress: z.string()
    .min(26, { message: "Input must be at least 26 characters" })
    .max(115, { message: "Input cannot exceed 115 characters" }),
    // Add Bitcoin address and extended public key format validation
    // .refine((val) => {
    //   const allowTestnet = config.env !== 'prod';
      
    //   // Bitcoin address validation regex
    //   // Supports legacy (1...), P2SH (3...), and Bech32 (bc1...) addresses
    //   const legacyRegex = /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/;
    //   const bech32Regex = /^bc1[a-z0-9]{39,59}$/;
    //   const testnetRegex = /^tb1[a-z0-9]{39,59}$/;
    //   const legacyTestnetRegex = /^[2mn][a-km-zA-HJ-NP-Z1-9]{25,34}$/;
      
    //   // Extended public key validation regex
    //   // xpub and ypub are 111 characters long and use Base58 encoding
    //   const xpubRegex = /^xpub[1-9A-HJ-NP-Za-km-z]{107}$/;
    //   const ypubRegex = /^ypub[1-9A-HJ-NP-Za-km-z]{107}$/;
    //   const tpubRegex = /^tpub[1-9A-HJ-NP-Za-km-z]{107}$/;
    //   const upubRegex = /^upub[1-9A-HJ-NP-Za-km-z]{107}$/;
      
    //   // Check if it's a valid Bitcoin address
    //   const isValidAddress = legacyRegex.test(val) || bech32Regex.test(val) || 
    //     (allowTestnet && (testnetRegex.test(val) || legacyTestnetRegex.test(val)));
      
    //   // Check if it's a valid extended public key
    //   const isValidExtendedKey = xpubRegex.test(val) || ypubRegex.test(val) ||
    //     (allowTestnet && (tpubRegex.test(val) || upubRegex.test(val)));
      
    //   return isValidAddress || isValidExtendedKey;
    // }, { message: "Please enter a valid Bitcoin address, xpub, or ypub" }),
  alertCondition: z.enum(["received", "sent", "both"], {
    required_error: "Please select when to be alerted",
  }),
  notificationMethod: z.enum(["email", "sms", "both"], {
    required_error: "Please select a notification method",
  }),
  emailAddress: z.string()
    .email({ message: "Please provide a valid email address" })
    .optional()
    .or(z.literal(''))
});

// Type for the form values
type WalletAlertFormValues = z.infer<typeof walletAlertSchema>;

interface WalletAlertFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WalletAlertForm({ open, onOpenChange }: WalletAlertFormProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  
  // Initialize form with default values
  const form = useForm<WalletAlertFormValues>({
    resolver: zodResolver(walletAlertSchema),
    defaultValues: {
      walletAddress: "",
      alertCondition: "both",
      notificationMethod: "email",
      emailAddress: ""
    }
  });

  // Handle dialog close - reset form when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset();
    }
    onOpenChange(newOpen);
  };
  
  // Watch notification method to conditionally show email field
  const notificationMethod = form.watch("notificationMethod");

  // Import hooks for API calls - but we'll use the mutation directly for better error handling
  const walletAlerts = useWalletAlerts();
  const queryClient = useQueryClient();
  
  // Create mutation directly to handle specific error cases
  const createAlertMutation = useMutation({
    mutationFn: createWalletAlert,
    onSuccess: () => {
      // Invalidate and refetch alerts after creating a new one
      queryClient.invalidateQueries({ queryKey: ['/api/wallet-alerts'] });
    },
  });
  
  // Handle form submission
  const onSubmit = async (values: WalletAlertFormValues) => {
    try {
      // Check if user has phone number when phone or both is selected
      if ((values.notificationMethod === 'sms' || values.notificationMethod === 'both') && 
          (!user?.phoneNumber || user.phoneNumber.trim() === '')) {
        toast({
          title: "Phone Number Required",
          description: "Please add a phone number to your profile before setting up SMS notifications.",
          variant: "destructive",
          action: (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                window.location.href = '/profile?tab=profile';
              }}
            >
              Update Profile
            </Button>
          ),
        });
        return;
      }
      
      // Clear any existing form errors
      form.clearErrors("walletAddress");
      
      // Extract the correct contact info for the API
      let contactInfo = '';
      if (values.notificationMethod === 'email') {
        contactInfo = values.emailAddress || '';
      } else if (values.notificationMethod === 'sms') {
        contactInfo = user?.phoneNumber || '';
      } else if (values.notificationMethod === 'both') {
        // For 'both', we'll use email as primary contact info
        // The backend should handle sending to both email and phone
        contactInfo = values.emailAddress || '';
      }
      
      // Send data to the server and wait for response
      await createAlertMutation.mutateAsync({
        address: values.walletAddress,
        cryptocurrency: 'bitcoin',
        alertType: values.alertCondition,
        notificationMethod: values.notificationMethod,
        contactInfo: contactInfo
      });
      
      // If we get here, the mutation was successful
      toast({
        title: "Alert Created",
        description: "Your wallet alert has been created successfully.",
        variant: "default",
      });
      
      // Close the modal (form will be reset automatically by handleOpenChange)
      onOpenChange(false);
    } catch (error: any) {
      console.error("Form submission error:", error);
      
      // Check if it's the alert limit reached error (429 status code or ALERT_LIMIT_REACHED error code)
      if (error?.status === 429 || error?.code === 'ALERT_LIMIT_REACHED' || error?.message?.includes('ALERT_LIMIT_REACHED')) {
        setShowLimitDialog(true);
      } else if (error?.status === 403 || error?.message?.toLowerCase().includes('invalid') || error?.message?.toLowerCase().includes('forbidden')) {
        // Set form error on the wallet address field
        form.setError("walletAddress", {
          type: "manual",
          message: "Invalid wallet address. Please enter a valid Bitcoin address, xPub, or yPub."
        });
      } else if (error?.message?.includes('already exists')) {
        toast({
          title: 'Error',
          description: 'An alert already exists for this address. Please delete the existing alert before creating a new one.',
          variant: 'destructive',
        });
      } else {
        // Show general error message for other errors
        toast({
          title: "Error",
          description: error?.message || "Failed to create wallet alert. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[500px] bg-white dark:bg-gray-900 border-none p-6">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white">Create New Wallet Alert</DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-300 mt-1">
              Set up notifications for when a wallet address receives or sends transactions.
            </DialogDescription>
          </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Wallet Address Field */}
            <FormField
              control={form.control}
              name="walletAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium text-gray-700 dark:text-gray-200">Wallet Address</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter a Bitcoin address, xPub, or yPub" 
                      className="rounded-md border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:border-blue-500 dark:placeholder:text-gray-400" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Alert Condition Radio Group */}
            <FormField
              control={form.control}
              name="alertCondition"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="font-medium text-gray-700 dark:text-gray-200">Alert Condition</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-2 dark:text-white"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="received" className="dark:border-gray-600 dark:text-white dark:data-[state=checked]:border-blue-400 dark:data-[state=checked]:bg-blue-700" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer dark:text-gray-300 hover:dark:text-white">
                          When wallet receives Bitcoin
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="sent" className="dark:border-gray-600 dark:text-white dark:data-[state=checked]:border-blue-400 dark:data-[state=checked]:bg-blue-700" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer dark:text-gray-300 hover:dark:text-white">
                          When wallet sends Bitcoin
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="both" className="dark:border-gray-600 dark:text-white dark:data-[state=checked]:border-blue-400 dark:data-[state=checked]:bg-blue-700" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer dark:text-gray-300 hover:dark:text-white">
                          Both sent and received transactions
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Notification Method Select */}
            <FormField
              control={form.control}
              name="notificationMethod"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium text-gray-700 dark:text-gray-200">Notification Method</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="dark:bg-gray-800 dark:border-gray-700 dark:text-white">
                        <SelectValue placeholder="Select a notification method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                      <SelectItem value="email" className="dark:text-gray-200 dark:hover:bg-gray-700">Email</SelectItem>
                      <SelectItem value="sms" className="dark:text-gray-200 dark:hover:bg-gray-700">SMS</SelectItem>
                      <SelectItem value="both" className="dark:text-gray-200 dark:hover:bg-gray-700">Both Email & SMS</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                  {(notificationMethod === "sms" || notificationMethod === "both") && 
                   (!user?.phoneNumber || user.phoneNumber.trim() === '') && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                      <span>⚠️</span>
                      SMS notifications require a phone number in your profile.
                    </p>
                  )}
                </FormItem>
              )}
            />
            
            {/* Phone Number Info - shown when SMS is selected */}
            {notificationMethod === "sms" && user?.phoneNumber && (
              <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-3 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  SMS notifications will be sent to: {user.phoneNumber}
                </p>
              </div>
            )}
            
            {/* Both Method Info - shown when both is selected */}
            {notificationMethod === "both" && (
              <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 p-3 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  Notifications will be sent to both your email and 
                  {user?.phoneNumber ? ` phone number (${user.phoneNumber})` : ' phone number (add to profile first)'}
                </p>
              </div>
            )}
            
            <DialogFooter className="flex justify-end gap-2 pt-4 mt-2">
              <DialogClose asChild>
                <Button type="button" variant="outline" size="sm" disabled={createAlertMutation.isPending}>
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" size="sm" disabled={createAlertMutation.isPending}>
                {createAlertMutation.isPending ? "Creating..." : "Create Alert"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
      </Dialog>
      
      <AlertLimitDialog 
        open={showLimitDialog} 
        onOpenChange={setShowLimitDialog} 
      />
    </>
  );
}